import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { stats } from '../mock/data';

const AnimatedNumber = ({ value, isInView }) => {
  const [count, setCount] = useState(0);
  const match = String(value).match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
  const prefix = match?.[1] ?? '';
  const numericPart = match?.[2] ?? null;
  const suffix = match?.[3] ?? '';
  const numValue = numericPart ? parseFloat(numericPart) : null;
  const decimalPlaces = numericPart && numericPart.includes('.') ? numericPart.split('.')[1].length : 0;

  useEffect(() => {
    if (!isInView || numValue === null) return;
    
    let start = 0;
    const duration = 2000;
    const increment = numValue / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= numValue) {
        setCount(numValue);
        clearInterval(timer);
      } else {
        setCount(decimalPlaces > 0 ? Number(start.toFixed(decimalPlaces)) : Math.floor(start));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [isInView, numValue, decimalPlaces]);

  return (
    <span>
      {numValue === null ? String(value) : `${prefix}${count.toFixed(decimalPlaces)}${suffix}`}
    </span>
  );
};

const Stats = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 relative overflow-hidden">
      {/* Animated background pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `
            repeating-linear-gradient(45deg, transparent, transparent 60px, rgba(255, 255, 255, 0.1) 60px, rgba(255, 255, 255, 0.1) 120px),
            repeating-linear-gradient(-45deg, transparent, transparent 60px, rgba(255, 255, 255, 0.05) 60px, rgba(255, 255, 255, 0.05) 120px)
          `
        }}
      />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 md:gap-8 max-w-lg sm:max-w-none mx-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.5, y: 50 }}
              animate={isInView ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.5, y: 50 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: 'spring',
                bounce: 0.4
              }}
              whileHover={{ 
                scale: 1.1, 
                y: -10,
                boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)'
              }}
              className="relative overflow-hidden text-center bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: 0.3 + index * 0.1, type: 'spring', bounce: 0.6 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 sm:mb-3"
                style={{ fontFamily: '"Space Grotesk", sans-serif' }}
              >
                <AnimatedNumber value={stat.value} isInView={isInView} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="text-sm md:text-base text-white/90 font-bold uppercase tracking-wider"
              >
                {stat.label}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;